<template>
    <tile class="flex flex-col gap-3">
        <div class="text-2xl">Bilag</div>
        <targeting-router-link v-if="displayTasksPendingApproval" :to="APPROVE_INCOMING" class="text-lg hover:text-primary pointer-cursor flex items-center gap-2">
            <i class="i-ph-check text-2xl text-primary" />
            <div>Til godkjenning</div>
            <div class="grow" />
            <div class="text-2xl text-primary">{{ documentsReadyForApproval.length }}</div>
        </targeting-router-link>
        <div class="text-lg hover:text-primary cursor-pointer flex items-center hover:border-b-primary gap-2" title="Åpne bilagsmottak" @click="setupIncomingTasksWithTransactions">
            <i class="i-ph-book-open text-2xl text-primary" />
            <div>Til bokføring</div>
            <div class="grow" />
            <div class="text-2xl text-primary">{{ documentsReadyForProcessing.length }}</div>
        </div>
        <USeparator />
        <div class="text-2xl">Betalinger</div>
        <targeting-router-link :to="PAYMENTS" class="text-lg hover:text-primary cursor-pointer flex justify-between items-center hover:border-b-primary gap-2" title="Åpne betalingsliste">
            <i class="i-ph-money text-2xl text-primary" />
            <div>Til godkjenning</div>
            <div class="grow" />
            <div class="text-2xl text-primary">{{ paymentsWaitingApprovalCount }}</div>
        </targeting-router-link>
    </tile>
</template>

<script setup lang="ts">

import Tile from '@/dashboard/Tile.vue'
import {PAYMENTS} from '@/bank'
import { TargetingRouterLink } from '@tritt/multipanel'
import {APPROVE_INCOMING, TASKS} from '@/task-manager'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'
import {usePanelManager} from '@tritt/multipanel'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import {listIncomingDocumentsV2} from '@/services/document.service'
import {ATTACHMENT_UPLOADED, ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {TRANSACTION_CREATED} from '@/types/transaction/TransactionEvent'
import {useEmitter} from '@/services/infrastructure/emitter'
import {IncomingDocument} from '@/types/IncomingDocument'
import {usePendingPayments} from '@/bank/payment.service'
import {PAYMENT_APPROVED, PAYMENT_CREATED, PAYMENT_UPDATED} from '@/bank/bank.events'
import {APPROVED, PENDING} from '@/task-manager/IncomingTaskStatus'
import {useSetting} from '@/setting/setting.service'

const {setPanelPath} = usePanelManager()
const {onEvents, cleanUp} = useEmitter()
const {fetch: fetchPendingTransactions, extendedPendingTransactions} = usePendingPayments()
const {setting} = useSetting<string>('DEFAULT_INCOMING_DOCUMENT_STATUS')

const displayTasksPendingApproval = computed(() => {
    return (setting.value?.value ?? 'APPROVED') === 'PENDING'
})

const setupIncomingTasksWithTransactions = async () => {
    await setPanelPath({componentName: TASKS.componentName, panelId: 1})
    await setPanelPath({componentName: CREATE_TRANSACTION.componentName, panelId: 2})
}

const paymentsWaitingApprovalCount = computed(() => {
    return extendedPendingTransactions.value.filter(pt => pt.paymentStatus === null).length
})

const documentsReadyForApproval = ref<Array<IncomingDocument>>([])
const documentsReadyForProcessing = ref<Array<IncomingDocument>>([])

const fetchDocuments = async () => {
    documentsReadyForProcessing.value = await listIncomingDocumentsV2(APPROVED)
    documentsReadyForApproval.value = await listIncomingDocumentsV2(PENDING)
}

onMounted(() => {
    fetchDocuments()
    onEvents([ATTACHMENT_UPLOADED, ORG_SWITCHED, ORG_SELECTED, TRANSACTION_CREATED], fetchDocuments)
    fetchPendingTransactions()
    onEvents([PAYMENT_CREATED, PAYMENT_APPROVED, PAYMENT_UPDATED, ORG_SWITCHED, ORG_SELECTED], fetchPendingTransactions)
})

onUnmounted(cleanUp)
</script>

<style scoped>

</style>